import contract from '../event-contract.json' with { type: 'json' };

export const CONTRACT = contract;
export const EVENT_MAP = new Map(contract.events.map((event) => [event.name, event]));
export const PIXEL_OWNED = new Set([
  'distinct_id',
  'event_source',
  'consent_basis',
  'shop_domain',
  'taxonomy_version',
]);
export const STOREFRONT_OWNED = new Set(['distinct_id', 'consent_basis', 'event_source', 'shop_domain']);

const GID_TYPES = new Set(['Product', 'ProductVariant', 'Order', 'OrderTransaction', 'Refund']);
const SECRET_KEY = /email|phone|telephone|address|customer|first.?name|last.?name|vin|checkout.?token|cart.?token|password|raw.?query/i;
const SENSITIVE_VALUE_FIELDS = new Set(['query_normalized', 'product_vendor', 'vehicle_brand', 'part_type', 'condition', 'sku', 'part_number_normalized', 'shipping_method_code', 'referrer_host']);
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VIN = /^[A-HJ-NPR-Z0-9]{17}$/i;
const PHONE = /^\+?[\d() .-]{7,20}$/;
const SAFE_FACET = /^[\p{L}\p{N}][\p{L}\p{N} ._/-]{0,79}$/u;
const ALLOWED_BRANDS = new Set([
  'audi', 'bmw', 'chevrolet', 'fiat', 'ford', 'genesis', 'honda', 'hyundai', 'kia', 'lexus',
  'mazda', 'mercedes', 'mercedes-benz', 'mini', 'nissan', 'porsche', 'subaru', 'tesla', 'toyota',
  'volkswagen', 'volvo',
]);

function typeMatches(value, expected) {
  const types = Array.isArray(expected) ? expected : [expected];
  return types.some((type) => {
    if (type === 'null') return value === null;
    if (type === 'array') return Array.isArray(value);
    if (type === 'object') return value !== null && typeof value === 'object' && !Array.isArray(value);
    if (type === 'integer') return Number.isSafeInteger(value);
    if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
    return typeof value === type;
  });
}

function sensitiveValue(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  const digitCount = (trimmed.match(/\d/g) || []).length;
  return EMAIL.test(trimmed) || VIN.test(trimmed) || /https?:\/\//i.test(trimmed) || (digitCount >= 10 && PHONE.test(trimmed));
}

function validateValue(value, schema, path, errors) {
  if (!typeMatches(value, schema.type)) {
    errors.push(`${path}:type`);
    return;
  }
  if (value === null) return;
  if (typeof value === 'string') {
    const propertyName = path.replace(/\[\d+\]$/, '').split('.').at(-1);
    if (SENSITIVE_VALUE_FIELDS.has(propertyName) && sensitiveValue(value)) errors.push(`${path}:sensitive-value`);
    if ((schema.minLength ?? 0) > value.length) errors.push(`${path}:minLength`);
    if ((schema.maxLength ?? Infinity) < value.length) errors.push(`${path}:maxLength`);
    if (schema.const !== undefined && value !== schema.const) errors.push(`${path}:const`);
    if (schema.enum && !schema.enum.includes(value)) errors.push(`${path}:enum`);
    if (schema.pattern && !new RegExp(schema.pattern).test(value)) errors.push(`${path}:pattern`);
    if (schema.format === 'date-time' && (!/^\d{4}-\d\d-\d\dT.*Z$/.test(value) || !Number.isFinite(Date.parse(value)))) errors.push(`${path}:date-time`);
  } else if (typeof value === 'number') {
    if (schema.minimum !== undefined && value < schema.minimum) errors.push(`${path}:minimum`);
    if (schema.exclusiveMinimum !== undefined && value <= schema.exclusiveMinimum) errors.push(`${path}:exclusiveMinimum`);
    if (schema.maximum !== undefined && value > schema.maximum) errors.push(`${path}:maximum`);
  } else if (Array.isArray(value)) {
    if ((schema.maxItems ?? Infinity) < value.length) errors.push(`${path}:maxItems`);
    if (schema.uniqueItems && new Set(value.map((item) => JSON.stringify(item))).size !== value.length) errors.push(`${path}:uniqueItems`);
    if (schema.items) value.forEach((item, index) => validateValue(item, schema.items, `${path}[${index}]`, errors));
  } else if (typeof value === 'object') {
    const properties = schema.properties ?? {};
    if (schema.additionalProperties === false) {
      for (const key of Object.keys(value)) if (!(key in properties)) errors.push(`${path}.${key}:unknown`);
    }
    for (const key of schema.required ?? []) if (!(key in value)) errors.push(`${path}.${key}:required`);
    for (const [key, item] of Object.entries(value)) if (properties[key]) validateValue(item, properties[key], `${path}.${key}`, errors);
  }
}

function conditionMatches(properties, condition) {
  for (const [key, expected] of Object.entries(condition ?? {})) {
    if (properties[key] !== expected) return false;
  }
  return true;
}

export function schemaForEvent(eventName) {
  const event = EVENT_MAP.get(eventName);
  if (!event) return null;
  const allowed = new Set([
    ...contract.common_required,
    ...contract.common_optional,
    ...event.required,
    ...event.optional,
  ]);
  const required = [...new Set([...contract.common_required, ...event.required])];
  return { event, allowed, required };
}

export function validateEvent(eventName, properties, { partial = false, source = 'theme_custom' } = {}) {
  const schema = schemaForEvent(eventName);
  const errors = [];
  if (!schema) return { ok: false, errors: ['event:unknown'] };
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return { ok: false, errors: ['properties:type'] };
  if (schema.event.source !== source) errors.push('event:source-mismatch');
  if (properties.event_source !== undefined && properties.event_source !== schema.event.source) errors.push('event_source:source-mismatch');

  for (const key of Object.keys(properties)) {
    if (!schema.allowed.has(key)) errors.push(`${key}:unknown`);
    if (SECRET_KEY.test(key)) errors.push(`${key}:sensitive-key`);
  }
  const required = partial
    ? schema.required.filter((key) => !STOREFRONT_OWNED.has(key))
    : schema.required;
  for (const key of required) if (!(key in properties)) errors.push(`${key}:required`);

  for (const key of Object.keys(properties)) {
    let base = contract.definitions[key];
    if (!base) continue;
    const override = schema.event.property_overrides?.[key] ?? {};
    if (schema.event.nullable_overrides?.includes(key)) base = { ...base, type: Array.isArray(base.type) ? [...base.type, 'null'] : [base.type, 'null'] };
    validateValue(properties[key], { ...base, ...override }, key, errors);
  }

  for (const rule of schema.event.conditional_required ?? []) {
    if (conditionMatches(properties, rule.when)) {
      for (const key of rule.fields) if (!(key in properties)) errors.push(`${key}:conditional-required`);
    }
  }

  if (source === 'theme_custom' && ['payment_captured', 'refund_completed'].includes(eventName)) errors.push('event:server-only');
  if (eventName === 'payment_captured' && properties.transaction_kind && !['SALE', 'CAPTURE'].includes(properties.transaction_kind)) errors.push('transaction_kind:payment-kind');
  if (eventName === 'refund_completed' && properties.transaction_kind && properties.transaction_kind !== 'REFUND') errors.push('transaction_kind:refund-kind');
  if (eventName.startsWith('checkout_') || eventName === 'shopify_checkout_completed') {
    const key = properties.checkout_key;
    if (key !== undefined && key !== null && !/^[a-f0-9]{64}$/.test(key)) errors.push('checkout_key:sha256');
  }

  const hasValue = properties.value !== undefined && properties.value !== null;
  const hasShipping = properties.shipping_value !== undefined && properties.shipping_value !== null;
  const hasCurrency = properties.currency !== undefined && properties.currency !== null;
  if ((hasValue || hasShipping) !== hasCurrency) errors.push('money+currency:pair');

  for (const key of ['product_id', 'variant_id', 'order_id', 'transaction_id', 'refund_id']) {
    const value = properties[key];
    if (typeof value === 'string' && value && !/^gid:\/\/shopify\/[A-Za-z]+\//.test(value)) errors.push(`${key}:gid`);
  }
  if (eventName === 'filter_changed') {
    const permitted = new Set(['user', 'dependency_reset']);
    if (properties.change_reason && !permitted.has(properties.change_reason)) errors.push('change_reason:enum');
  }

  return { ok: errors.length === 0, errors };
}

export function makeEventId(randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto)) {
  if (typeof randomUUID !== 'function') return null;
  try {
    const value = randomUUID();
    return typeof value === 'string' && value.length <= 128 ? value : null;
  } catch {
    return null;
  }
}

export function canonicalDistinctId(shopDomain, clientId) {
  if (typeof shopDomain !== 'string' || typeof clientId !== 'string' || !shopDomain || !clientId) return null;
  const value = `shopify:${shopDomain.toLowerCase()}:client:${clientId}`;
  return value.length <= 200 ? value : null;
}

export function normalizeShopifyGid(value, resourceType) {
  if (typeof value !== 'string' || !value || typeof resourceType !== 'string' || !GID_TYPES.has(resourceType)) return null;
  if (/^gid:\/\/shopify\/[A-Za-z]+\/[A-Za-z0-9_-]+$/.test(value)) {
    const type = value.split('/')[3];
    return type === resourceType ? value : null;
  }
  if (/^\d+$/.test(value)) return `gid://shopify/${resourceType}/${value}`;
  return null;
}

export function safePagePath(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return '/';
  const path = pathname.split(/[?#]/, 1)[0].replace(/\/{2,}/g, '/');
  if (/^\/(?:account|orders|checkouts?)(?:\/|$)/i.test(path)) return `/${path.split('/')[1]}/*`;
  return path.slice(0, 256) || '/';
}

export function classifyQuery(rawValue, allowedValues = []) {
  if (typeof rawValue !== 'string' || !rawValue.trim()) return { query_type: 'empty', query_redacted: false };
  const normalized = rawValue.normalize('NFKC').trim().replace(/\s+/g, ' ');
  if (normalized === '*') return { query_type: 'empty', query_redacted: false };
  if (EMAIL.test(normalized) || VIN.test(normalized) || PHONE.test(normalized) || /https?:\/\//i.test(normalized)) {
    return { query_type: 'unknown', query_redacted: true };
  }
  if (ALLOWED_BRANDS.has(normalized.toLocaleLowerCase('en-US'))) {
    return { query_type: 'brand', query_normalized: normalized.toLocaleLowerCase('en-US'), query_redacted: false };
  }
  const allowSet = new Set(allowedValues.filter((value) => typeof value === 'string').map((value) => value.normalize('NFKC').trim().toLocaleLowerCase('en-US')));
  if (allowSet.has(normalized.toLocaleLowerCase('en-US'))) {
    return { query_type: /[0-9]/.test(normalized) ? 'part_number' : 'keyword', query_normalized: normalized.toLocaleLowerCase('en-US'), query_redacted: false };
  }
  const looksLikePartNumber = normalized.length >= 3 && normalized.length <= 32 && /[0-9]/.test(normalized) && /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(normalized);
  return { query_type: looksLikePartNumber ? 'part_number' : 'keyword', query_redacted: true };
}

export function sanitizeTargetUrl(input) {
  let url;
  try { url = new URL(input, 'https://store.invalid'); } catch { return null; }
  const path = safePagePath(url.pathname);
  const params = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (/^(?:q|query)$/i.test(key)) {
      const query = classifyQuery(value);
      if (query.query_normalized) params.set(key, query.query_normalized);
    } else if (/^(?:sort_by|page|options\[prefix\])$/i.test(key)) {
      params.append(key, value.slice(0, 128));
    } else if (/^filter\.[a-z0-9_.-]+$/i.test(key)) {
      const normalized = value.normalize('NFKC').trim().replace(/\s+/g, ' ');
      if (SAFE_FACET.test(normalized) && !EMAIL.test(normalized) && !VIN.test(normalized) && !PHONE.test(normalized)) params.append(key, normalized);
    }
  }
  return `${path}${params.size ? `?${params.toString()}` : ''}`;
}

export function moneyFromShopifyCents(value, currencyCode) {
  const cents = typeof value === 'number' ? value : typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value) ? Number(value) : NaN;
  if (!Number.isFinite(cents) || cents < 0 || !/^[A-Z]{3}$/.test(currencyCode ?? '')) return null;
  const amount = cents / 100;
  return Number.isFinite(amount) ? { value: amount, currency: currencyCode } : null;
}

export function safeReplayContext(posthog, allowedHost = 'https://us.posthog.com', projectId = '639039') {
  if (!posthog || typeof posthog.get_session_replay_url !== 'function') return { replay_status: 'unavailable' };
  try {
    const raw = posthog.get_session_replay_url();
    if (!raw) return { replay_status: 'unavailable' };
    const url = new URL(raw);
    const expected = new URL(allowedHost);
    if (url.protocol !== 'https:' || url.origin !== expected.origin || !url.pathname.startsWith(`/project/${projectId}/replay/`)) return { replay_status: 'unavailable' };
    const id = url.pathname.split('/').filter(Boolean).at(-1);
    return {
      replay_session_id: /^[A-Za-z0-9_-]{1,128}$/.test(id ?? '') ? id : undefined,
      replay_url: `${url.origin}${url.pathname}`.slice(0, 512),
      replay_status: 'available',
    };
  } catch {
    return { replay_status: 'unavailable' };
  }
}

export function utf8Bytes(value) {
  return new TextEncoder().encode(value).byteLength;
}

export function stripOptionalForLimit(properties, maxBytes = 8192) {
  const next = { ...properties };
  const drop = ['replay_url', 'query_normalized', 'sku', 'part_number_normalized', 'product_vendor', 'vehicle_brand', 'part_type', 'condition', 'variant_id', 'product_id'];
  for (const key of drop) {
    if (utf8Bytes(JSON.stringify(next)) <= maxBytes) break;
    delete next[key];
  }
  return utf8Bytes(JSON.stringify(next)) <= maxBytes ? next : null;
}
